import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, 
  TrendingDown, 
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  BarChart3,
  LineChart as LineChartIcon,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart, BarChart, Bar } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Forecasts = () => {
  // State management
  const [activeTab, setActiveTab] = useState('standard'); // 'standard' or 'store-operations'
  const [metric, setMetric] = useState('sales');
  const [scopeType, setScopeType] = useState('organization');
  const [scopeId, setScopeId] = useState('');
  const [horizon, setHorizon] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  
  // Store Operations state
  const [storeOpsLoading, setStoreOpsLoading] = useState(false);
  const [storeOpsData, setStoreOpsData] = useState(null);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  
  // Category Planner state
  const [categoryPlannerLoading, setCategoryPlannerLoading] = useState(false);
  const [categoryPlannerData, setCategoryPlannerData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Price Elasticity state
  const [priceElasticityLoading, setPriceElasticityLoading] = useState(false);
  const [priceElasticityData, setPriceElasticityData] = useState(null);
  const [elasticityScopeType, setElasticityScopeType] = useState('organization');
  const [elasticityScopeId, setElasticityScopeId] = useState('');
  const [elasticityCategory, setElasticityCategory] = useState('');
  
  // Procurement & Replenishment state
  const [procurementLoading, setProcurementLoading] = useState(false);
  const [procurementData, setProcurementData] = useState(null);
  const [planningHorizon, setPlanningHorizon] = useState(7);
  const [procurementStoreFilter, setProcurementStoreFilter] = useState('');
  const [procurementVendorFilter, setProcurementVendorFilter] = useState('');
  const [procurementPriorityFilter, setProcurementPriorityFilter] = useState('all');

  // Dynamic data from uploads
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [dynamicStores, setDynamicStores] = useState([]);

  // Fetch dynamic categories and stores on mount
  React.useEffect(() => {
    const fetchDynamicData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        
        const [catRes, storeRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/forecasting/dynamic/categories`, { headers }),
          axios.get(`${BACKEND_URL}/api/forecasting/dynamic/stores`, { headers })
        ]);
        
        setDynamicCategories(catRes.data?.categories || []);
        setDynamicStores(storeRes.data?.stores || []);
      } catch (err) {
        console.error('Failed to fetch dynamic data:', err);
      }
    };
    fetchDynamicData();
  }, []);

  // Metric options
  const metricOptions = [
    { value: 'sales', label: 'Sales Forecast', icon: '💰' },
    { value: 'inventory', label: 'Inventory Forecast', icon: '📦' },
    { value: 'footfall', label: 'Footfall Forecast', icon: '👥' },
    { value: 'returns', label: 'Returns Forecast', icon: '↩️' },
    { value: 'plan_target', label: 'Plan vs Target', icon: '🎯' }
  ];

  // Scope type options
  const scopeTypeOptions = [
    { value: 'organization', label: 'Organization-wide' },
    { value: 'store', label: 'By Store' },
    { value: 'category', label: 'By Category' },
    { value: 'product', label: 'By Product (SKU)' }
  ];

  // Horizon options
  const horizonOptions = [7, 14, 30, 60, 90];
  
  // Category options - from uploaded data
  const categoryOptions = dynamicCategories.length > 0 ? dynamicCategories : ['All Categories'];

  // Generate standard forecast - USING PROFESSIONAL ENGINE
  const handleGenerateForecast = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // Build query params
      const params = new URLSearchParams();
      params.append('metric', metric);
      if (scopeType === 'store' && scopeId) params.append('store_id', scopeId);
      if (scopeType === 'category' && scopeId) params.append('category', scopeId);
      
      // Use professional forecasting endpoint
      const url = `${BACKEND_URL}/api/forecasting/professional/standard?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setForecastData({
        ...response.data,
        metric,
        scopeType,
        scopeId,
        horizon
      });
      
    } catch (err) {
      console.error('Forecast error:', err);
      setError(err.response?.data?.detail || 'Failed to generate forecast. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Generate store operations forecast - USING PROFESSIONAL ENGINE
  const handleGenerateStoreOpsForecast = async () => {
    setStoreOpsLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedStoreId) params.append('store_id', selectedStoreId);
      
      const url = `${BACKEND_URL}/api/forecasting/professional/store-operations?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setStoreOpsData(response.data);
      
    } catch (err) {
      console.error('Store ops forecast error:', err);
      setError(err.response?.data?.detail || 'Failed to generate store operations forecast.');
    } finally {
      setStoreOpsLoading(false);
    }
  };
  
  // Generate category planner forecast
  const handleGenerateCategoryPlannerForecast = async () => {
    if (!selectedCategory) {
      setError('Please select a Category');
      return;
    }
    
    setCategoryPlannerLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      
      const url = `${BACKEND_URL}/api/forecasting/professional/category-planner?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setCategoryPlannerData(response.data);
      
    } catch (err) {
      console.error('Category planner forecast error:', err);
      setError(err.response?.data?.detail || 'Failed to generate category planner forecast.');
    } finally {
      setCategoryPlannerLoading(false);
    }
  };
  
  // Generate price elasticity forecast - USING PROFESSIONAL ENGINE
  const handleGeneratePriceElasticityForecast = async () => {
    setPriceElasticityLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // Build query params
      const params = new URLSearchParams();
      params.append('scope_type', elasticityScopeType);
      if (elasticityScopeType === 'category' && elasticityCategory) {
        params.append('scope_id', elasticityCategory);
      } else if (elasticityScopeType !== 'organization' && elasticityScopeId) {
        params.append('scope_id', elasticityScopeId);
      }
      
      const url = `${BACKEND_URL}/api/forecasting/professional/price-elasticity?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setPriceElasticityData(response.data);
      
    } catch (err) {
      console.error('Price elasticity forecast error:', err);
      setError(err.response?.data?.detail || 'Failed to generate price elasticity forecast.');
    } finally {
      setPriceElasticityLoading(false);
    }
  };
  
  // Generate procurement & replenishment plan - USING PROFESSIONAL ENGINE
  const handleGenerateProcurementPlan = async () => {
    setProcurementLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // Build query params
      const params = new URLSearchParams();
      params.append('planning_horizon', planningHorizon.toString());
      if (procurementStoreFilter) params.append('store_filter', procurementStoreFilter);
      if (procurementVendorFilter) params.append('vendor_filter', procurementVendorFilter);
      
      const url = `${BACKEND_URL}/api/forecasting/professional/procurement?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setProcurementData(response.data);
      
    } catch (err) {
      console.error('Procurement planning error:', err);
      setError(err.response?.data?.detail || 'Failed to generate procurement plan.');
    } finally {
      setProcurementLoading(false);
    }
  };

  // Extract forecast values for chart - UPDATED FOR PROFESSIONAL ENGINE
  const getChartData = () => {
    if (!forecastData) return [];
    
    // Professional engine returns chart_data directly
    if (forecastData.chart_data && forecastData.chart_data.length > 0) {
      return forecastData.chart_data;
    }
    
    // Build chart data from forecasts object
    if (forecastData.forecasts) {
      return Object.values(forecastData.forecasts).map(f => ({
        horizon: `${f.horizon_days}d`,
        days: f.horizon_days,
        forecast: f.forecasted_revenue || 0,
        revenue: f.forecasted_revenue || 0,
        units: f.forecasted_units || 0,
        margin: f.forecasted_margin,
        margin_status: f.margin_status || (f.forecasted_margin != null ? 'OK' : 'INSUFFICIENT_COST_DATA'),
        confidence: (f.confidence || 0) * 100,
        trend: f.trend_direction || 'stable'
      })).sort((a, b) => a.days - b.days);
    }
    
    return [];
  };

  // Get confidence badge color
  const getConfidenceBadge = (confidence) => {
    const colors = {
      high: 'bg-green-100 text-green-800 border-green-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-red-100 text-red-800 border-red-300'
    };
    // Handle both string and numeric confidence values
    let level = 'medium';
    if (typeof confidence === 'number') {
      level = confidence >= 80 ? 'high' : confidence >= 60 ? 'medium' : 'low';
    } else if (typeof confidence === 'string') {
      level = confidence.toLowerCase();
    }
    return colors[level] || colors.medium;
  };

  // Get confidence level text from number or string
  const getConfidenceText = (confidence) => {
    if (typeof confidence === 'number') {
      return `${confidence.toFixed(0)}%`;
    }
    if (typeof confidence === 'string') {
      return confidence;
    }
    return 'Medium';
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    switch(trend?.toLowerCase()) {
      case 'increasing':
        return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'decreasing':
        return <TrendingDown className="h-5 w-5 text-red-600" />;
      default:
        return <Activity className="h-5 w-5 text-blue-600" />;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Forecasts</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            AI-driven forecasting across retail metrics with actionable insights
          </p>
        </div>
        <Badge variant="outline" className="px-3 py-1">
          <BarChart3 className="h-4 w-4 mr-1" />
          Powered by AI
        </Badge>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-2 border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setActiveTab('standard')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'standard'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          Standard Forecasts
        </button>
        <button
          onClick={() => setActiveTab('store-operations')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'store-operations'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          Store Operations
        </button>
        <button
          onClick={() => setActiveTab('category-planner')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'category-planner'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          Category Planner
        </button>
        <button
          onClick={() => setActiveTab('price-elasticity')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'price-elasticity'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          Price & Discount
        </button>
        <button
          onClick={() => setActiveTab('procurement')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'procurement'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          Procurement & Replenishment
        </button>
      </div>

      {/* Standard Forecasts Tab */}
      {activeTab === 'standard' && (
        <>
          {/* Filter Panel */}
          <Card>
        <CardHeader>
          <CardTitle className="text-lg">Forecast Configuration</CardTitle>
          <CardDescription>Select metric, scope, and horizon to generate forecast</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Metric Selector */}
            <div className="space-y-2">
              <Label>Metric</Label>
              <Select value={metric} onValueChange={setMetric}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {metricOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.icon} {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Scope Type */}
            <div className="space-y-2">
              <Label>Scope Type</Label>
              <Select value={scopeType} onValueChange={setScopeType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {scopeTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Scope ID (conditional) */}
            {scopeType !== 'organization' && (
              <div className="space-y-2">
                <Label>Scope ID / Name</Label>
                <Input
                  placeholder={`Enter ${scopeType} ID...`}
                  value={scopeId}
                  onChange={(e) => setScopeId(e.target.value)}
                />
              </div>
            )}

            {/* Horizon Selector */}
            <div className="space-y-2">
              <Label>Primary Horizon (Days)</Label>
              <div className="flex gap-2">
                {horizonOptions.map(h => (
                  <Button
                    key={h}
                    variant={horizon === h ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHorizon(h)}
                    className="flex-1"
                  >
                    {h}d
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="mt-6">
            <Button 
              onClick={handleGenerateForecast}
              disabled={loading}
              className="w-full md:w-auto"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating Forecast...
                </>
              ) : (
                <>
                  <LineChartIcon className="mr-2 h-4 w-4" />
                  Generate Forecast
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Forecast Results */}
      {forecastData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Trend Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Forecast Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-3">
                  {getTrendIcon(forecastData.trajectory?.direction)}
                  <div>
                    <p className="text-2xl font-bold capitalize">
                      {forecastData.trajectory?.direction || 'Stable'}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Growth: {forecastData.trajectory?.growth_rate || 0}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Confidence Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Confidence Level
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-8 w-8 text-blue-600" />
                  <div>
                    <Badge className={getConfidenceBadge(forecastData.trajectory?.confidence)}>
                      {getConfidenceText(forecastData.trajectory?.confidence)}
                    </Badge>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      Based on agent intelligence
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Revenue Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  30-Day Forecast
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-green-600">
                  ₹{(forecastData.forecasts?.['30_days']?.forecasted_revenue || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  {(forecastData.forecasts?.['30_days']?.forecasted_units || 0).toLocaleString()} units
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Horizon Forecast Table */}
          <Card>
            <CardHeader>
              <CardTitle>Forecast by Horizon</CardTitle>
              <CardDescription>Multi-horizon forecast comparison</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="forecast-horizon-table">
                  <thead>
                    <tr className="border-b bg-slate-50 dark:bg-slate-800">
                      <th className="px-4 py-3 text-left font-semibold">Horizon</th>
                      <th className="px-4 py-3 text-right font-semibold">Revenue</th>
                      <th className="px-4 py-3 text-right font-semibold">Units</th>
                      <th className="px-4 py-3 text-right font-semibold">Margin</th>
                      <th className="px-4 py-3 text-center font-semibold">Confidence</th>
                      <th className="px-4 py-3 text-center font-semibold">Stock Risk</th>
                      <th className="px-4 py-3 text-right font-semibold">Reorder Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forecastData.forecasts && Object.entries(forecastData.forecasts).map(([key, f]) => (
                      <tr key={key} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-4 py-3 font-medium">{f.horizon_days} Days</td>
                        <td className="px-4 py-3 text-right text-green-600 font-medium">₹{(f.forecasted_revenue || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                        <td className="px-4 py-3 text-right">{(f.forecasted_units || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">{f.forecasted_margin != null ? `₹${f.forecasted_margin.toLocaleString(undefined, {maximumFractionDigits: 0})}` : <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Cost Data Missing</span>}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={getConfidenceBadge(f.confidence_level)}>{f.confidence_level}</Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={
                            f.stockout_risk === 'high' ? 'bg-red-100 text-red-800' :
                            f.stockout_risk === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }>{f.stockout_risk}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right">{(f.reorder_quantity || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Forecast Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Forecast Visualization</CardTitle>
              <CardDescription>
                {metricOptions.find(m => m.value === metric)?.label} across selected horizons
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={getChartData()}>
                    <defs>
                      <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorMargin" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="horizon" />
                    <YAxis />
                    <Tooltip formatter={(value) => `₹${Number(value).toLocaleString()}`} />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      name="Revenue"
                      stroke="#3b82f6" 
                      fill="url(#colorForecast)"
                      strokeWidth={2}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="margin" 
                      name="Margin"
                      stroke="#10b981" 
                      fill="url(#colorMargin)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Risks and Actions Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risks Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="mr-2 h-5 w-5 text-orange-600" />
                  Risk Assessment
                </CardTitle>
                <CardDescription>
                  {forecastData.risks?.length || 0} risks identified
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {forecastData.risks?.map((risk, idx) => (
                    <div key={idx} className="flex items-start space-x-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <Badge className={
                        risk.severity === 'high' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }>{risk.severity}</Badge>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{risk.description}</p>
                        <p className="text-xs text-slate-500 mt-1">{risk.action}</p>
                      </div>
                    </div>
                  ))}
                  {(!forecastData.risks || forecastData.risks.length === 0) && (
                    <p className="text-sm text-slate-500">No significant risks identified</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recommended Actions Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CheckCircle2 className="mr-2 h-5 w-5" />
                  Recommended Actions
                </CardTitle>
                <CardDescription>
                  {forecastData.actions?.length || 0} actionable recommendations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {forecastData.actions?.map((action, idx) => (
                    <div key={idx} className="flex items-start space-x-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
                      <Badge variant={action.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                        {action.priority?.toUpperCase()}
                      </Badge>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{action.action}</p>
                        <p className="text-xs text-slate-500 mt-1">{action.description}</p>
                      </div>
                    </div>
                  ))}
                  {(!forecastData.actions || forecastData.actions.length === 0) && (
                    <p className="text-sm text-slate-500">No immediate actions required</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Demand Drivers */}
          {forecastData.demand_drivers && (
            <Card>
              <CardHeader>
                <CardTitle>Demand Drivers</CardTitle>
                <CardDescription>Key factors influencing the forecast</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(forecastData.demand_drivers).map(([key, value]) => (
                    <div key={key} className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="text-lg font-bold mt-1">{typeof value === 'number' ? (value * 100).toFixed(0) + '%' : value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Metadata Footer */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between text-sm text-slate-600 dark:text-slate-400">
                <div>
                  <span className="font-medium">Metric:</span> {metricOptions.find(m => m.value === metric)?.label}
                </div>
                <div>
                  <span className="font-medium">Scope:</span> {scopeType} {scopeId && `(${scopeId})`}
                </div>
                <div>
                  <span className="font-medium">Generated:</span> {forecastData.generated_at ? new Date(forecastData.generated_at).toLocaleString() : 'N/A'}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Empty State */}
      {!forecastData && !loading && !error && (
        <Card className="text-center py-12">
          <CardContent>
            <LineChartIcon className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              No Forecast Generated Yet
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Configure your forecast parameters above and click &quot;Generate Forecast&quot; to see AI-driven predictions
            </p>
          </CardContent>
        </Card>
      )}
        </>
      )}

      {/* Store Operations Tab */}
      {activeTab === 'store-operations' && (
        <>
          {/* Store Operations Filter Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Store Operations Forecast</CardTitle>
              <CardDescription>
                Comprehensive operational forecast for a specific store including sales, footfall, inventory, and risk assessment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Store ID</Label>
                  {dynamicStores.length > 0 ? (
                    <select 
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                      value={selectedStoreId}
                      onChange={(e) => setSelectedStoreId(e.target.value)}
                      data-testid="store-operations-store-select"
                    >
                      <option value="">Select a store...</option>
                      {dynamicStores.map(s => (
                        <option key={s.store_id} value={s.store_id}>
                          {s.store_id} - {s.store_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      placeholder="Enter Store ID"
                      value={selectedStoreId}
                      onChange={(e) => setSelectedStoreId(e.target.value)}
                    />
                  )}
                </div>
              </div>

              <div className="mt-6">
                <Button 
                  onClick={handleGenerateStoreOpsForecast}
                  disabled={storeOpsLoading || !selectedStoreId}
                  className="w-full md:w-auto"
                  size="lg"
                >
                  {storeOpsLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing Store Operations...
                    </>
                  ) : (
                    <>
                      <LineChartIcon className="mr-2 h-4 w-4" />
                      Generate Store Forecast
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Store Operations Results */}
          {storeOpsData && !storeOpsData.error && (
            <>
              {/* Summary Banner */}
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                        {storeOpsData.summary?.total_stores || 0} Store(s) Analyzed
                      </h2>
                      <p className="text-slate-600 dark:text-slate-400 mt-1">
                        30-Day Forecast Revenue: ₹{(storeOpsData.summary?.total_forecast_revenue_30d || 0).toLocaleString()}
                        {' | '}Units: {(storeOpsData.summary?.total_forecast_units_30d || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-red-600 dark:text-red-400">
                        {storeOpsData.summary?.high_risk_stores || 0}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        High Risk Stores
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Store Forecasts Table */}
              {storeOpsData.store_forecasts?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Store Forecast Details</CardTitle>
                    <CardDescription>Detailed operational forecast per store</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {storeOpsData.store_forecasts.map((sf, idx) => (
                        <div key={idx} className="border rounded-lg p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h3 className="text-lg font-semibold">{sf.store_name || sf.store_id}</h3>
                              <p className="text-sm text-slate-500">Store ID: {sf.store_id}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={
                                sf.risk_score === 'high' ? 'bg-red-100 text-red-800' :
                                sf.risk_score === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }>
                                Risk: {sf.risk_score?.toUpperCase()}
                              </Badge>
                              <Badge className={
                                sf.trend === 'improving' ? 'bg-green-100 text-green-800' :
                                sf.trend === 'declining' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }>
                                {sf.trend === 'improving' ? <TrendingUp className="h-3 w-3 mr-1 inline" /> :
                                 sf.trend === 'declining' ? <TrendingDown className="h-3 w-3 mr-1 inline" /> : null}
                                {sf.trend}
                              </Badge>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                              <p className="text-xs text-slate-500">7-Day Revenue</p>
                              <p className="text-lg font-bold text-blue-700">₹{(sf.forecast_7d?.revenue || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                              <p className="text-xs text-slate-500">30-Day Revenue</p>
                              <p className="text-lg font-bold text-green-700">₹{(sf.forecast_30d?.revenue || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                            </div>
                            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 text-center">
                              <p className="text-xs text-slate-500">30-Day Units</p>
                              <p className="text-lg font-bold text-purple-700">{(sf.forecast_30d?.units || 0).toLocaleString()}</p>
                            </div>
                            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 text-center">
                              <p className="text-xs text-slate-500">30-Day Footfall</p>
                              <p className="text-lg font-bold text-orange-700">{(sf.forecast_30d?.footfall || 0).toLocaleString()}</p>
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-center">
                              <p className="text-xs text-slate-500">Conversion</p>
                              <p className="text-lg font-bold">{(sf.forecast_30d?.conversion || 0).toFixed(1)}%</p>
                            </div>
                          </div>

                          {sf.required_stock_transfer > 0 && (
                            <div className="mt-2 text-sm text-orange-700 bg-orange-50 dark:bg-orange-900/20 rounded p-2">
                              Stock Transfer Required: {sf.required_stock_transfer.toLocaleString()} units
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Store Revenue Chart */}
              {storeOpsData.store_forecasts?.length > 1 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Store Revenue Comparison (30-Day Forecast)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={storeOpsData.store_forecasts.map(sf => ({
                          name: sf.store_name || sf.store_id,
                          revenue: sf.forecast_30d?.revenue || 0,
                          units: sf.forecast_30d?.units || 0,
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis />
                          <Tooltip formatter={(value) => `₹${Number(value).toLocaleString()}`} />
                          <Legend />
                          <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Transfer Recommendations */}
              {storeOpsData.transfer_recommendations?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Stock Transfer Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50 dark:bg-slate-800">
                            <th className="px-4 py-2 text-left">SKU</th>
                            <th className="px-4 py-2 text-left">From</th>
                            <th className="px-4 py-2 text-left">To</th>
                            <th className="px-4 py-2 text-right">Qty</th>
                            <th className="px-4 py-2 text-center">Priority</th>
                          </tr>
                        </thead>
                        <tbody>
                          {storeOpsData.transfer_recommendations.map((tr, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="px-4 py-2">{tr.sku || tr.product}</td>
                              <td className="px-4 py-2">{tr.from_store}</td>
                              <td className="px-4 py-2">{tr.to_store}</td>
                              <td className="px-4 py-2 text-right">{tr.quantity}</td>
                              <td className="px-4 py-2 text-center">
                                <Badge className={
                                  tr.priority === 'HIGH' ? 'bg-red-100 text-red-800' :
                                  tr.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }>{tr.priority}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              {storeOpsData.actions?.length > 0 && (
                <Card className="border-orange-200 dark:border-orange-800">
                  <CardHeader>
                    <CardTitle className="flex items-center text-orange-700 dark:text-orange-400">
                      <AlertTriangle className="mr-2 h-5 w-5" />
                      Priority Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {storeOpsData.actions.map((action, idx) => (
                        <div key={idx} className="flex items-start space-x-3 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 rounded-lg">
                          <Badge variant="destructive" className="text-xs">{action.priority?.toUpperCase()}</Badge>
                          <div>
                            <p className="text-sm font-medium">{action.action}</p>
                            <p className="text-xs text-slate-500 mt-1">{action.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Error State for Store Ops */}
          {storeOpsData && storeOpsData.error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {storeOpsData.error}
                {storeOpsData.available_stores?.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium">Available Store IDs:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {storeOpsData.available_stores.map((sid, idx) => (
                        <Badge key={idx} variant="outline" className="cursor-pointer" onClick={() => setSelectedStoreId(String(sid))}>
                          {sid}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Empty State for Store Ops */}
          {!storeOpsData && !storeOpsLoading && !error && (
            <Card className="text-center py-12">
              <CardContent>
                <LineChartIcon className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  No Store Operations Forecast Generated
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  Enter a Store ID and click &quot;Generate Store Forecast&quot; to see comprehensive operational insights
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Category/Merchandise Planner Tab */}
      {activeTab === 'category-planner' && (
        <>
          {/* Category Planner Filter Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Category/Merchandise Planner Forecast</CardTitle>
              <CardDescription>
                Comprehensive planning forecast for categories including demand, margin, sell-through, and plan vs target analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map(cat => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-6">
                <Button 
                  onClick={handleGenerateCategoryPlannerForecast}
                  disabled={categoryPlannerLoading}
                  className="w-full md:w-auto"
                  size="lg"
                >
                  {categoryPlannerLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing Category Planning...
                    </>
                  ) : (
                    <>
                      <LineChartIcon className="mr-2 h-4 w-4" />
                      Generate Category Forecast
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Category Planner Results */}
          {categoryPlannerData && !categoryPlannerData.error && categoryPlannerData.status !== 'insufficient_data' && (
            <>
              {/* Category Info Banner */}
              <Card className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                        {categoryPlannerData.scope_id}
                      </h2>
                      <p className="text-slate-600 dark:text-slate-400 mt-1">
                        Category Planning Forecast • 30/60/90 Day Horizons
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                        {categoryPlannerData.historical_summary?.sell_through_rate?.toFixed(1)}%
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        Current Sell-Through
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Key Metrics Grid - 30 Day Forecast */}
              {categoryPlannerData.horizons && categoryPlannerData.horizons[0] && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        30-Day Demand
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div>
                        <p className="text-2xl font-bold">
                          {categoryPlannerData.horizons[0].demand_units_forecast?.toLocaleString()} units
                        </p>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                          ₹{categoryPlannerData.horizons[0].demand_value_forecast?.toLocaleString()}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        Margin Forecast
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div>
                        <p className="text-2xl font-bold">
                          {categoryPlannerData.horizons[0].margin_forecast?.margin_pct?.toFixed(1)}%
                        </p>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                          ₹{categoryPlannerData.horizons[0].margin_forecast?.margin_value?.toLocaleString()}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        Sell-Through Projection
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div>
                        <p className="text-2xl font-bold">
                          {categoryPlannerData.horizons[0].sell_through_projection?.toFixed(1)}%
                        </p>
                        <Badge className={`mt-2 ${
                          categoryPlannerData.horizons[0].sell_through_projection > 80 ? 'bg-green-100 text-green-800' :
                          categoryPlannerData.horizons[0].sell_through_projection > 60 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {categoryPlannerData.horizons[0].sell_through_projection > 80 ? 'Strong' :
                           categoryPlannerData.horizons[0].sell_through_projection > 60 ? 'Moderate' :
                           'Weak'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        Plan vs Target
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div>
                        {categoryPlannerData.horizons[0].plan_vs_target_vs_forecast?.has_data ? (
                          <>
                            <p className="text-sm">Plan: ₹{categoryPlannerData.horizons[0].plan_vs_target_vs_forecast.plan?.toLocaleString()}</p>
                            <p className="text-sm">Target: ₹{categoryPlannerData.horizons[0].plan_vs_target_vs_forecast.target?.toLocaleString()}</p>
                          </>
                        ) : (
                          <p className="text-sm text-slate-500">No plan/target data</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Historical Summary */}
              {categoryPlannerData.historical_summary && (
                <Card>
                  <CardHeader>
                    <CardTitle>Historical Performance (90 Days)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Avg Daily Sales</p>
                        <p className="text-lg font-semibold">
                          {categoryPlannerData.historical_summary.avg_daily_sales_units?.toFixed(0)} units
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Avg Daily Value</p>
                        <p className="text-lg font-semibold">
                          ₹{categoryPlannerData.historical_summary.avg_daily_sales_value?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Current Margin</p>
                        <p className="text-lg font-semibold">
                          {categoryPlannerData.historical_summary.current_margin_pct?.toFixed(1)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Current Inventory</p>
                        <p className="text-lg font-semibold">
                          {categoryPlannerData.historical_summary.current_inventory_units?.toLocaleString()} units
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Sell-Through Rate</p>
                        <p className="text-lg font-semibold">
                          {categoryPlannerData.historical_summary.sell_through_rate?.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Insights and Actions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Activity className="mr-2 h-5 w-5" />
                      Planning Insights
                    </CardTitle>
                    <CardDescription>
                      {categoryPlannerData.insights?.length || 0} key findings for planners
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {categoryPlannerData.insights?.map((insight, idx) => (
                        <div key={idx} className="flex items-start space-x-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center text-xs font-semibold text-purple-700 dark:text-purple-300">
                              {idx + 1}
                            </div>
                          </div>
                          <p className="text-sm text-slate-700 dark:text-slate-300">
                            {insight}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle2 className="mr-2 h-5 w-5" />
                      Recommended Actions
                    </CardTitle>
                    <CardDescription>
                      {categoryPlannerData.recommended_actions?.length || 0} planner actions
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {categoryPlannerData.recommended_actions?.map((action, idx) => (
                        <div key={idx} className="flex items-start space-x-3 p-3 border border-purple-200 dark:border-purple-700 rounded-lg">
                          <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-700 dark:text-slate-300">
                            {action}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Risks */}
              {categoryPlannerData.risks && categoryPlannerData.risks.length > 0 && (
                <Card className="border-orange-200 dark:border-orange-800">
                  <CardHeader>
                    <CardTitle className="flex items-center text-orange-700 dark:text-orange-400">
                      <AlertTriangle className="mr-2 h-5 w-5" />
                      Category Risks
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {categoryPlannerData.risks.map((risk, idx) => (
                        <Alert key={idx} variant="destructive" className="bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700">
                          <AlertDescription className="text-orange-800 dark:text-orange-300">
                            {risk}
                          </AlertDescription>
                        </Alert>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Insufficient Data Message */}
          {categoryPlannerData && categoryPlannerData.status === 'insufficient_data' && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {categoryPlannerData.message}
                <div className="mt-2 space-y-1">
                  {categoryPlannerData.recommended_actions?.map((action, idx) => (
                    <p key={idx} className="text-sm">• {action}</p>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Error State for Category Planner */}
          {categoryPlannerData && categoryPlannerData.error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{categoryPlannerData.error}</AlertDescription>
            </Alert>
          )}

          {/* Empty State for Category Planner */}
          {!categoryPlannerData && !categoryPlannerLoading && !error && (
            <Card className="text-center py-12">
              <CardContent>
                <LineChartIcon className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  No Category Forecast Generated
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  Select a category and click &quot;Generate Category Forecast&quot; to see comprehensive planning insights
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Price & Discount Tab */}
      {activeTab === 'price-elasticity' && (
        <>
          {/* Configuration Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Price Elasticity Configuration</CardTitle>
              <CardDescription>Select scope to analyze price elasticity and optimal discounts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Scope Type */}
                <div className="space-y-2">
                  <Label>Scope Type</Label>
                  <Select value={elasticityScopeType} onValueChange={setElasticityScopeType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="organization">Organization-wide</SelectItem>
                      <SelectItem value="store">By Store</SelectItem>
                      <SelectItem value="category">By Category</SelectItem>
                      <SelectItem value="product">By Product</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Scope ID */}
                {elasticityScopeType !== 'organization' && (
                  <div className="space-y-2">
                    <Label>
                      {elasticityScopeType === 'store' && 'Store ID'}
                      {elasticityScopeType === 'category' && 'Category'}
                      {elasticityScopeType === 'product' && 'Product ID'}
                    </Label>
                    {elasticityScopeType === 'category' ? (
                      <Select value={elasticityCategory} onValueChange={setElasticityCategory}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category..." />
                        </SelectTrigger>
                        <SelectContent>
                          {categoryOptions.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder={`Enter ${elasticityScopeType} ID...`}
                        value={elasticityScopeId}
                        onChange={(e) => setElasticityScopeId(e.target.value)}
                      />
                    )}
                  </div>
                )}

                {/* Generate Button */}
                <div className="space-y-2">
                  <Label>&nbsp;</Label>
                  <Button 
                    onClick={handleGeneratePriceElasticityForecast}
                    disabled={priceElasticityLoading}
                    className="w-full"
                  >
                    {priceElasticityLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <BarChart3 className="mr-2 h-4 w-4" />
                        Generate Analysis
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Display */}
          {priceElasticityData && priceElasticityData.status === 'success' && (
            <>
              {/* Summary Card */}
              <Card className="border-2 border-blue-200 dark:border-blue-800">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                        {priceElasticityData.scope_id}
                      </h2>
                      <p className="text-slate-600 dark:text-slate-400 mt-1">
                        Price Elasticity & Discount Optimization • 30/60/90 Day Horizons
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                        {priceElasticityData.elasticity_summary?.overall_elasticity?.toFixed(2)}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        Price Elasticity
                      </p>
                      <Badge className="mt-2 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {priceElasticityData.elasticity_summary?.elasticity_category}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Optimal Discount Bands */}
              {priceElasticityData.optimal_discount_bands && priceElasticityData.optimal_discount_bands.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                    Optimal Discount Bands
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {priceElasticityData.optimal_discount_bands.map((band, idx) => (
                      <Card key={idx} className="border-2 hover:border-blue-400 transition-colors">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center justify-between">
                            {band.band_name}
                            <Badge variant={
                              band.band_name === 'Conservative' ? 'outline' :
                              band.band_name === 'Moderate' ? 'default' : 'destructive'
                            }>
                              {band.discount_pct}%
                            </Badge>
                          </CardTitle>
                          <CardDescription className="text-xs">
                            {band.recommended_for}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600 dark:text-slate-400">Volume Lift:</span>
                              <span className="font-semibold text-green-600">+{band.expected_volume_lift_pct}%</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600 dark:text-slate-400">Margin Impact:</span>
                              <span className={`font-semibold ${
                                band.margin_impact === 'minimal' ? 'text-green-600' :
                                band.margin_impact === 'moderate' ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                {band.margin_impact}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* 30-Day Forecast Metrics */}
              {priceElasticityData.horizons && priceElasticityData.horizons[0] && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                    30-Day Forecast with Optimal Discount
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          Recommended Discount
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold text-blue-600">
                          {priceElasticityData.horizons[0].recommended_discount_pct}%
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          Expected Volume Lift
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold text-green-600">
                          +{priceElasticityData.horizons[0].expected_volume_lift_pct}%
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          Forecasted Revenue
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div>
                          <p className="text-xl font-bold">
                            ₹{priceElasticityData.horizons[0].forecasted_revenue?.toLocaleString()}
                          </p>
                          <Badge className={`mt-1 ${
                            priceElasticityData.horizons[0].revenue_vs_baseline >= 0 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {priceElasticityData.horizons[0].revenue_vs_baseline >= 0 ? '+' : ''}
                            {priceElasticityData.horizons[0].revenue_vs_baseline}% vs baseline
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          Forecasted Margin
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">
                          {priceElasticityData.horizons[0].forecasted_margin_pct?.toFixed(1)}%
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              {/* Historical Performance */}
              {priceElasticityData.historical_summary && (
                <Card>
                  <CardHeader>
                    <CardTitle>Historical Performance Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Avg Price</p>
                        <p className="text-lg font-semibold">
                          ₹{priceElasticityData.historical_summary.avg_price?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Total Revenue</p>
                        <p className="text-lg font-semibold">
                          ₹{priceElasticityData.historical_summary.total_revenue?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Current Margin</p>
                        <p className="text-lg font-semibold">
                          {priceElasticityData.historical_summary.current_margin_pct?.toFixed(1)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Promotion Effectiveness</p>
                        <p className="text-lg font-semibold">
                          {priceElasticityData.promotion_effectiveness?.toFixed(0)}/100
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Insights and Actions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Activity className="mr-2 h-5 w-5" />
                      Pricing Insights
                    </CardTitle>
                    <CardDescription>
                      {priceElasticityData.insights?.length || 0} key findings
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {priceElasticityData.insights?.map((insight, idx) => (
                        <div key={idx} className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-xs font-semibold text-blue-700 dark:text-blue-300">
                              {idx + 1}
                            </div>
                          </div>
                          <p className="text-sm text-slate-700 dark:text-slate-300">
                            {insight}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle2 className="mr-2 h-5 w-5" />
                      Recommended Actions
                    </CardTitle>
                    <CardDescription>
                      {priceElasticityData.recommended_actions?.length || 0} optimization actions
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {priceElasticityData.recommended_actions?.map((action, idx) => (
                        <div key={idx} className="flex items-start space-x-3 p-3 border border-blue-200 dark:border-blue-700 rounded-lg">
                          <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-700 dark:text-slate-300">
                            {action}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Risks */}
              {priceElasticityData.risks && priceElasticityData.risks.length > 0 && (
                <Card className="border-orange-200 dark:border-orange-800">
                  <CardHeader>
                    <CardTitle className="flex items-center text-orange-700 dark:text-orange-400">
                      <AlertTriangle className="mr-2 h-5 w-5" />
                      Pricing Risks
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {priceElasticityData.risks.map((risk, idx) => (
                        <Alert key={idx} variant="destructive" className="bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700">
                          <AlertDescription className="text-orange-800 dark:text-orange-300">
                            {risk}
                          </AlertDescription>
                        </Alert>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Insufficient Data Message */}
          {priceElasticityData && priceElasticityData.status === 'insufficient_data' && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {priceElasticityData.message}
                <div className="mt-2 space-y-1">
                  {priceElasticityData.recommended_actions?.map((action, idx) => (
                    <p key={idx} className="text-sm">• {action}</p>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Error State */}
          {priceElasticityData && priceElasticityData.status === 'error' && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{priceElasticityData.error || priceElasticityData.message}</AlertDescription>
            </Alert>
          )}

          {/* Empty State */}
          {!priceElasticityData && !priceElasticityLoading && !error && (
            <Card className="text-center py-12">
              <CardContent>
                <BarChart3 className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  No Price Elasticity Analysis Generated
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  Select scope and click &quot;Generate Analysis&quot; to see price elasticity and optimal discount recommendations
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Procurement & Replenishment Tab */}
      {activeTab === 'procurement' && (
        <>
          {/* Configuration Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Procurement & Replenishment Planning</CardTitle>
              <CardDescription>AI-driven procurement and replenishment recommendations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {/* Planning Horizon */}
                <div className="space-y-2">
                  <Label>Planning Horizon</Label>
                  <Select value={planningHorizon.toString()} onValueChange={(val) => setPlanningHorizon(parseInt(val))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 Days</SelectItem>
                      <SelectItem value="14">14 Days</SelectItem>
                      <SelectItem value="30">30 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Store Filter */}
                <div className="space-y-2">
                  <Label>Store ID (Optional)</Label>
                  <Input
                    placeholder="Filter by store..."
                    value={procurementStoreFilter}
                    onChange={(e) => setProcurementStoreFilter(e.target.value)}
                  />
                </div>

                {/* Vendor Filter */}
                <div className="space-y-2">
                  <Label>Vendor Code (Optional)</Label>
                  <Input
                    placeholder="Filter by vendor..."
                    value={procurementVendorFilter}
                    onChange={(e) => setProcurementVendorFilter(e.target.value)}
                  />
                </div>

                {/* Priority Filter */}
                <div className="space-y-2">
                  <Label>Priority (Optional)</Label>
                  <Select value={procurementPriorityFilter || "all"} onValueChange={setProcurementPriorityFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All priorities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="HIGH">High Priority</SelectItem>
                      <SelectItem value="MEDIUM">Medium Priority</SelectItem>
                      <SelectItem value="LOW">Low Priority</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Generate Button */}
                <div className="space-y-2">
                  <Label>&nbsp;</Label>
                  <Button 
                    onClick={handleGenerateProcurementPlan}
                    disabled={procurementLoading}
                    className="w-full"
                  >
                    {procurementLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Planning...
                      </>
                    ) : (
                      <>
                        <BarChart3 className="mr-2 h-4 w-4" />
                        Generate Plan
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Display */}
          {procurementData && procurementData.status === 'success' && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      Replenishment Items
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">
                        {procurementData.summary?.total_replenishment_items || 0}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        Store-SKU combinations
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="destructive" className="text-xs">
                          {procurementData.summary?.replenishment_by_priority?.HIGH || 0} High
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {procurementData.summary?.replenishment_by_priority?.MEDIUM || 0} Med
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      Vendor Replenishment Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <p className="text-2xl font-bold text-green-600">
                        {procurementData.summary?.total_procurement_orders || 0}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        Vendors to contact
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="destructive" className="text-xs">
                          {procurementData.summary?.procurement_by_priority?.HIGH || 0} Urgent
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      Total Quantity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      {procurementData.summary?.total_replenishment_qty?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      Units to replenish
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      Est. Procurement Cost
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      ₹{procurementData.summary?.total_procurement_cost?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      Total PO value
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Replenishment Plan Table */}
              {procurementData.replenishment_plan && procurementData.replenishment_plan.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Replenishment Plan ({procurementData.planning_horizon_days} Days)</span>
                      <Badge>{procurementData.replenishment_plan.length} Items</Badge>
                    </CardTitle>
                    <CardDescription>
                      Store-SKU level recommendations with priorities
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="border-b">
                          <tr className="text-left">
                            <th className="pb-2 font-semibold">Priority</th>
                            <th className="pb-2 font-semibold">Store</th>
                            <th className="pb-2 font-semibold">Product</th>
                            <th className="pb-2 font-semibold text-right">Current</th>
                            <th className="pb-2 font-semibold text-right">In Transit</th>
                            <th className="pb-2 font-semibold text-right">Forecast</th>
                            <th className="pb-2 font-semibold text-right">Replenish Qty</th>
                            <th className="pb-2 font-semibold text-right">Days to Stockout</th>
                            <th className="pb-2 font-semibold">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {procurementData.replenishment_plan.slice(0, 20).map((item, idx) => (
                            <tr key={idx} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                              <td className="py-3">
                                <Badge variant={
                                  item.priority === 'HIGH' ? 'destructive' :
                                  item.priority === 'MEDIUM' ? 'default' : 'outline'
                                } className="text-xs">
                                  {item.priority}
                                </Badge>
                              </td>
                              <td className="py-3 font-mono text-xs">{item.store_id}</td>
                              <td className="py-3 font-mono text-xs">{item.product_id}</td>
                              <td className="py-3 text-right">{item.current_stock}</td>
                              <td className="py-3 text-right">{item.in_transit}</td>
                              <td className="py-3 text-right">{item.forecasted_demand}</td>
                              <td className="py-3 text-right font-semibold text-blue-600">{item.recommended_qty}</td>
                              <td className="py-3 text-right">
                                <span className={
                                  item.days_until_stockout < 7 ? 'text-red-600 font-semibold' :
                                  item.days_until_stockout < 14 ? 'text-yellow-600' :
                                  'text-green-600'
                                }>
                                  {item.days_until_stockout}d
                                </span>
                              </td>
                              <td className="py-3 text-xs text-slate-600 dark:text-slate-400 max-w-xs truncate">
                                {item.reason}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {procurementData.replenishment_plan.length > 20 && (
                        <p className="text-sm text-slate-500 mt-3 text-center">
                          Showing top 20 of {procurementData.replenishment_plan.length} items
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Procurement Plan (Vendor POs) */}
              {procurementData.procurement_plan && procurementData.procurement_plan.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Procurement Plan - Replenishment Requirements</span>
                      <Badge variant="outline">{procurementData.procurement_plan.length} Vendors</Badge>
                    </CardTitle>
                    <CardDescription>
                      Vendor-level consolidated replenishment requirements
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {procurementData.procurement_plan.slice(0, 10).map((po, idx) => (
                        <Card key={idx} className="border-2 hover:border-blue-300 transition-colors">
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <CardTitle className="text-base font-mono">
                                  {po.vendor_id}
                                </CardTitle>
                                <Badge variant={
                                  po.priority === 'HIGH' ? 'destructive' :
                                  po.priority === 'MEDIUM' ? 'default' : 'outline'
                                }>
                                  {po.priority}
                                </Badge>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-green-600">
                                  ₹{po.total_po_cost?.toLocaleString()}
                                </p>
                                <p className="text-xs text-slate-500">{po.total_requirement_qty || po.total_po_qty} units</p>
                              </div>
                            </div>
                            <CardDescription className="text-xs mt-2">
                              {po.reason}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm font-semibold border-b pb-2">
                                <span>Product</span>
                                <span>Quantity</span>
                              </div>
                              {po.products.slice(0, 5).map((product, pidx) => (
                                <div key={pidx} className="flex justify-between text-sm">
                                  <span className="font-mono text-xs">{product.product_id}</span>
                                  <span className="font-semibold">{product.replenishment_requirement_qty || product.recommended_po_qty} units</span>
                                </div>
                              ))}
                              {po.products.length > 5 && (
                                <p className="text-xs text-slate-500 italic">
                                  +{po.products.length - 5} more products
                                </p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {procurementData.procurement_plan.length > 10 && (
                        <p className="text-sm text-slate-500 text-center">
                          Showing top 10 of {procurementData.procurement_plan.length} vendors
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Insights and Actions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Activity className="mr-2 h-5 w-5" />
                      Planning Insights
                    </CardTitle>
                    <CardDescription>
                      {procurementData.insights?.length || 0} key findings
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {procurementData.insights?.map((insight, idx) => (
                        <div key={idx} className="flex items-start space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center text-xs font-semibold text-green-700 dark:text-green-300">
                              {idx + 1}
                            </div>
                          </div>
                          <p className="text-sm text-slate-700 dark:text-slate-300">
                            {insight}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CheckCircle2 className="mr-2 h-5 w-5" />
                      Recommended Actions
                    </CardTitle>
                    <CardDescription>
                      {procurementData.recommended_actions?.length || 0} action items
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {procurementData.recommended_actions?.map((action, idx) => (
                        <div key={idx} className="flex items-start space-x-3 p-3 border border-green-200 dark:border-green-700 rounded-lg">
                          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-700 dark:text-slate-300">
                            {action}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Risks */}
              {procurementData.risks && procurementData.risks.length > 0 && (
                <Card className="border-orange-200 dark:border-orange-800">
                  <CardHeader>
                    <CardTitle className="flex items-center text-orange-700 dark:text-orange-400">
                      <AlertTriangle className="mr-2 h-5 w-5" />
                      Procurement Risks
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {procurementData.risks.map((risk, idx) => (
                        <Alert key={idx} variant="destructive" className="bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700">
                          <AlertDescription className="text-orange-800 dark:text-orange-300">
                            {risk}
                          </AlertDescription>
                        </Alert>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Insufficient Data Message */}
          {procurementData && procurementData.status === 'insufficient_data' && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {procurementData.message}
                <div className="mt-2 space-y-1">
                  {procurementData.recommended_actions?.map((action, idx) => (
                    <p key={idx} className="text-sm">• {action}</p>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Error State */}
          {procurementData && procurementData.status === 'error' && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{procurementData.error || procurementData.message}</AlertDescription>
            </Alert>
          )}

          {/* Empty State */}
          {!procurementData && !procurementLoading && !error && (
            <Card className="text-center py-12">
              <CardContent>
                <BarChart3 className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  No Procurement Plan Generated
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  Configure planning horizon and click &quot;Generate Plan&quot; to see AI-driven procurement and replenishment recommendations
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default Forecasts;
